/**
 * `gamification-core` — projection of Bodega SYNC AUDITING to GameEvents.
 *
 * Gamification is DERIVED from the `activity` log (`activity_recent` bucket,
 * `entity_type = "sync"` entries emitted by the BYOS engine on complete/fail/
 * resolve/restore). Adds no tables and does not mutate the oplog: `applyEvents` is pure and
 * deterministic -> the same feed produces the same `PlayerState` (local on client,
 * recomputable on server for fraud prevention).
 */
import { type ActivityRow, parseActivityPatch } from "../activity/format";
import { applyEvents, initialState } from "./engine";
import { BODEGA_EVENTS, bodegaGamificationConfig } from "./config";
import { dayKeyUTC } from "./streaks";
import type { GameEvent, PlayerState } from "./types";

/** `client_ts` (honest local time) if present; otherwise `server_ts`. */
function timestampOf(row: Pick<ActivityRow, "client_ts" | "server_ts">): number | null {
  const raw = row.client_ts ?? row.server_ts;
  if (!raw) {
    return null;
  }
  const t = Date.parse(raw);
  return Number.isNaN(t) ? null : t;
}

/** Numeric value of `patch.<field>.new`, or 0 if missing / not a number. */
function patchNumber(row: Pick<ActivityRow, "patch">, field: string): number {
  const change = parseActivityPatch(row.patch)[field];
  const n = Number(change?.new);
  return Number.isFinite(n) ? n : 0;
}

/** The `status` recorded in the patch of a sync entry. */
function syncStatus(row: Pick<ActivityRow, "patch">): string | null {
  const change = parseActivityPatch(row.patch).status;
  return typeof change?.new === "string" ? change.new : null;
}

/**
 * Translates the sync log into gamification events (ascending chronological
 * order, as required by the streak reducer). Irrelevant entries are ignored; a
 * failed sync grants NO reward (only achieved work counts).
 */
export function deriveGameEvents(rows: readonly ActivityRow[]): GameEvent[] {
  const events: GameEvent[] = [];
  for (const row of rows) {
    if (row.entity_type !== "sync") {
      continue;
    }
    const at = timestampOf(row);
    if (at === null) {
      continue;
    }
    const day = dayKeyUTC(at);
    const base = { at, day, meta: { activityId: row.id } };

    switch (syncStatus(row)) {
      case "completed": {
        // Volume = files moved (job metadata; never file content).
        const moved = patchNumber(row, "uploaded") + patchNumber(row, "downloaded");
        events.push({ type: BODEGA_EVENTS.syncCompleted, value: moved, ...base });
        break;
      }
      case "conflict-resolved": {
        events.push({ type: BODEGA_EVENTS.conflictResolved, ...base });
        break;
      }
      case "restored": {
        events.push({ type: BODEGA_EVENTS.versionRestored, ...base });
        break;
      }
      default: {
        // "failed" and unknown statuses do not generate events.
        break;
      }
    }
  }
  // Feed arrives server_ts DESC; reducer requires ASC chronological order.
  return events.toSorted((a, b) => a.at - b.at);
}

/**
 * Game state derived from the sync log. Pure: same feed -> same
 * state. Returns XP/level/streaks/badges/quests ready for the UI.
 */
export function deriveGameState(rows: readonly ActivityRow[]): PlayerState {
  const events = deriveGameEvents(rows);
  return applyEvents(initialState(), events, bodegaGamificationConfig).state;
}
